import {
	ParticipantTileProps,
	useEnsureTrackRef,
	useParticipantTile,
	AudioTrack,
	TrackMutedIndicator,
	ParticipantName,
	ConnectionQualityIndicator,
	TrackRefContext,
	isTrackReference,
	BarVisualizer,
} from "@livekit/components-react";
import React from "react";

/**
 * The `ParticipantAudioTile` component is the base utility wrapper for displaying a visual representation of a participant.
 * This component can be used as a child of the `TileLoop` or independently if a participant is passed as a property.
 *
 * @example
 * ```tsx
 * <ParticipantAudioTile />
 * ```
 * @public
 */
export const ParticipantAudioTile: (
	props: ParticipantTileProps & React.RefAttributes<HTMLDivElement>
) => React.ReactNode = /* @__PURE__ */ React.forwardRef<HTMLDivElement, ParticipantTileProps>(
	function ParticipantAudioTile(
		{
			children,
			disableSpeakingIndicator,
			onParticipantClick,
			trackRef,
			...htmlProps
		}: ParticipantTileProps,
		ref
	) {
		const trackReference = useEnsureTrackRef(trackRef);
		const { elementProps } = useParticipantTile({
			trackRef: trackReference,
			htmlProps,
			disableSpeakingIndicator,
			onParticipantClick,
		});

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const AudioTrackComponent = AudioTrack as any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const TrackMutedIndicatorComponent = TrackMutedIndicator as any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const ParticipantNameComponent = ParticipantName as any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const ConnectionQualityIndicatorComponent = ConnectionQualityIndicator as any;

		return (
			<div ref={ref} style={{ position: "relative", minHeight: "4vh" }} {...elementProps}>
				<TrackRefContext.Provider value={trackReference}>
					{children ?? (
						<>
							{isTrackReference(trackReference) && (
								<AudioTrackComponent trackRef={trackReference}></AudioTrackComponent>
							)}

							<div className="lk-participant-metadata flex flex-row gap-2  h-fit">
								<div className="text-gray-700 flex flex-row gap-2 items-end ">
									<BarVisualizer
										barCount={3}
										className="lk-audio-bar-visualizer"
										options={{ minHeight: 3, maxHeight: 25 }}
										style={{
											maxWidth: "20px",
											padding: 0,
											margin: 0,
										}}
									/>
									<TrackMutedIndicatorComponent
										trackRef={trackReference}
									></TrackMutedIndicatorComponent>
									<ParticipantNameComponent />
								</div>
								<ConnectionQualityIndicatorComponent className="lk-participant-metadata-item " />
							</div>
						</>
					)}
				</TrackRefContext.Provider>
			</div>
		);
	}
);
